import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail, inr, priceLabel } from "@/lib/api";
import { SEO } from "@/components/SEO";

const EMPTY = { name: "", description: "", price_type: "fixed", price: 0, travel_charge: "", addons: [], active: true, order: 0 };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/services").then((r) => setServices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...EMPTY, ...s, travel_charge: s.travel_charge ?? "", addons: s.addons || [] });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: form.price_type === "custom" ? null : Number(form.price) || 0,
      travel_charge: form.travel_charge === "" || form.travel_charge == null ? null : Number(form.travel_charge),
      order: Number(form.order) || 0,
      addons: form.addons.map((a) => ({ name: a.name, price: Number(a.price) || 0 })),
    };
    try {
      if (editing) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editing ? "Service updated — live on the website now" : "Service added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/services/${s.id}`);
      toast.success("Service deleted");
      load();
    } catch { toast.error("Could not delete service"); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/services/${s.id}`, { ...s, travel_charge: s.travel_charge ?? null, active: !s.active });
      load();
    } catch { toast.error("Could not update service"); }
  };

  const inputCls = "rounded-xl bg-ivory/5 border-ivory/15 text-ivory h-11";

  return (
    <div data-testid="admin-services-page">
      <SEO title="Services & Rates" description="The Velvet Brush admin." path="/admin/services" />
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif font-bold text-2xl sm:text-3xl">Services & Rates</h1>
          <p className="mt-1 text-sm text-ivory/50">Rates update on the public website immediately.</p>
        </div>
        <Button onClick={openNew} data-testid="add-service-btn" className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Add Service
        </Button>
      </div>

      <div className="mt-8 flex flex-col gap-3">
        {services.map((s) => (
          <div key={s.id} data-testid={`admin-service-row-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="bg-ivory/5 border border-ivory/10 rounded-2xl p-4 sm:p-5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-serif text-lg leading-tight truncate">{s.name}</h3>
              <p className="text-xs text-ivory/50 mt-1">
                {priceLabel(s) || "—"}
                {s.travel_charge != null && ` • + ${inr(s.travel_charge)} travel`}
                {(s.addons || []).length > 0 && ` • ${s.addons.length} add-on${s.addons.length > 1 ? "s" : ""}`}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <label className="hidden sm:flex items-center gap-2 text-xs text-ivory/50">
                <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`service-active-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                {s.active ? "Live" : "Hidden"}
              </label>
              <button onClick={() => openEdit(s)} data-testid={`edit-service-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="p-2 text-ivory/60 hover:text-velvet"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(s)} data-testid={`delete-service-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="p-2 text-ivory/60 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#262525] text-ivory border-ivory/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit Service" : "Add Service"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="flex flex-col gap-4 mt-2">
            <div>
              <Label>Service Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="service-name-input" className={inputCls} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="service-desc-input" className="rounded-xl bg-ivory/5 border-ivory/15 text-ivory min-h-[80px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Price Type</Label>
                <Select value={form.price_type} onValueChange={(v) => setForm((f) => ({ ...f, price_type: v }))}>
                  <SelectTrigger data-testid="service-price-type" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed" data-testid="ptype-fixed">Fixed Price</SelectItem>
                    <SelectItem value="starting" data-testid="ptype-starting">Starting Price</SelectItem>
                    <SelectItem value="custom" data-testid="ptype-custom">Custom Quote</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.price_type !== "custom" && (
                <div>
                  <Label>Price (₹)</Label>
                  <Input type="number" min="0" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} data-testid="service-price-input" className={inputCls} />
                </div>
              )}
              <div>
                <Label>Travel Charge (₹, optional)</Label>
                <Input type="number" min="0" value={form.travel_charge} onChange={(e) => setForm((f) => ({ ...f, travel_charge: e.target.value }))} data-testid="service-travel-input" className={inputCls} />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} data-testid="service-order-input" className={inputCls} />
              </div>
            </div>
            <div>
              <Label>Add-on Pricing</Label>
              <div className="mt-2 flex flex-col gap-2">
                {form.addons.map((a, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={a.name} placeholder="Add-on name" data-testid={`addon-name-${i}`}
                      onChange={(e) => setForm((f) => { const ao = [...f.addons]; ao[i] = { ...ao[i], name: e.target.value }; return { ...f, addons: ao }; })}
                      className={inputCls} />
                    <Input type="number" min="0" value={a.price} placeholder="₹" data-testid={`addon-price-${i}`}
                      onChange={(e) => setForm((f) => { const ao = [...f.addons]; ao[i] = { ...ao[i], price: e.target.value }; return { ...f, addons: ao }; })}
                      className={`${inputCls} w-28`} />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, addons: f.addons.filter((_, j) => j !== i) }))}
                      data-testid={`addon-remove-${i}`} className="p-2 text-ivory/50 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setForm((f) => ({ ...f, addons: [...f.addons, { name: "", price: 0 }] }))}
                  data-testid="addon-add-btn" className="self-start text-xs font-semibold text-velvet hover:text-velvethover">
                  + Add add-on
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm" data-testid="service-active-label">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="service-active-toggle" /> Active (visible on site)
            </label>
            <Button type="submit" disabled={saving} data-testid="service-save-btn"
              className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold h-12">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : editing ? "Save Changes" : "Add Service"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
